import React, {useState, useEffect, useCallback} from 'react';
import {Button, Dialog, DialogBody, Icon, MenuItem, Spinner, Tag, Tooltip} from '@blueprintjs/core';
import {Select} from '@blueprintjs/select';

import {cameraServers, recordings} from '/src/services/api';
import {device} from '/src/services/iot-api';
import {errorToast, successToast} from '/src/utils/toaster';

const STEP_CHOICE = 'choice';
const STEP_MOVE = 'move';
const STEP_DETACH = 'detach';

const MOVE_SELECT = 'select';
const MOVE_CONFIRM = 'confirm';
const MOVE_DONE = 'done';

const MoveStep = ({recording, onClose}) => {
    const [devices, setDevices] = useState(null);
    const [selected, setSelected] = useState(null);
    const [subStep, setSubStep] = useState(MOVE_SELECT);
    const [loading, setLoading] = useState(false);

    const organisationId = recording.organisation?._id;
    const organisationName = recording.organisation?.name;
    const locationId = recording.location?._id;
    const locationName = recording.location?.name;
    const currentDeviceName = recording.cameraServer?.name;

    useEffect(() => {
        if (!organisationId || !locationId) {
            setDevices([]);
            return;
        }
        cameraServers.list({
            withinOrganisations: organisationId,
            include: 'recordings',
        }).then(devs => {
            // Exclude the current device if any, and only show devices in the same location
            const currentDeviceId = recording.cameraServer?._id;
            setDevices(devs.filter(d =>
                d.id !== currentDeviceId && d.location === locationId
            ));
        }).catch(() => {
            errorToast({message: 'Failed to load devices'});
        });
    }, []);

    const selectedRecs = selected?.recordings ?? [];

    const handleMove = useCallback(() => {
        if (!selected) return;
        setLoading(true);

        recordings.update(recording.id, {cameraServer: selected.id})
            .then(() => {
                successToast({message: `Moved ${recording.name} to ${selected.name}`});
                // Fire-and-forget: notify devices to update their config
                if (currentDeviceName) {
                    device.command('UpdateHandledRecids', currentDeviceName, null, 'update').catch(() => {});
                }
                device.command('UpdateHandledRecids', selected.name, null, 'update').catch(() => {});
                successToast({message: 'Sent device update commands'});
                setSubStep(MOVE_DONE);
            })
            .catch(err => {
                errorToast({message: err?.message ?? 'Failed to move recording'});
            })
            .finally(() => setLoading(false));
    }, [selected, recording, currentDeviceName]);

    if (devices == null) return <Spinner size={24} />;

    if (!devices.length) return (
        <Tag minimal intent="warning" icon="warning-sign">No devices available in this organisation and location</Tag>
    );

    if (subStep === MOVE_SELECT) {
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75em'}}>
                <p style={{margin: 0}}>
                    <Tooltip position="right" content={<div style={{maxWidth: 220}}>The target device must belong to <strong>{organisationName}</strong> and be in <strong>{locationName}</strong>.</div>}>
                        <Icon icon="info-sign" style={{marginRight: 6, cursor: 'help'}} />
                    </Tooltip>
                    Select device for <strong>{recording.name}</strong>:
                </p>

                <Select
                    items={devices}
                    itemPredicate={(query, dev) => dev.name.toLowerCase().includes(query.toLowerCase())}
                    itemRenderer={(dev, {handleClick, modifiers}) => (
                        <MenuItem
                            key={dev.id}
                            text={dev.name}
                            label={`${dev.recordings?.length ?? 0} recs`}
                            onClick={handleClick}
                            active={modifiers.active}
                            selected={selected?.id === dev.id}
                        />
                    )}
                    onItemSelect={setSelected}
                    popoverProps={{minimal: true, matchTargetWidth: true}}
                    fill
                >
                    <Button
                        fill
                        text={selected ? selected.name : 'Select a device...'}
                        endIcon="caret-down"
                        icon={selected ? 'desktop' : undefined}
                    />
                </Select>

                {selected && (
                    <div style={{fontSize: 13, color: '#5c7080'}}>
                        <strong>{selected.name}</strong> currently has {selectedRecs.length} recording{selectedRecs.length !== 1 ? 's' : ''}
                    </div>
                )}

                <Button
                    fill
                    intent="primary"
                    icon={currentDeviceName ? 'arrow-right' : 'add'}
                    text={selected ? `${currentDeviceName ? 'Move' : 'Attach'} to ${selected.name}` : (currentDeviceName ? 'Move' : 'Attach')}
                    disabled={!selected}
                    onClick={() => setSubStep(MOVE_CONFIRM)}
                />
            </div>
        );
    }

    if (subStep === MOVE_CONFIRM) {
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75em'}}>
                <p style={{margin: 0}}>
                    <strong>Step 1:</strong> {currentDeviceName ? 'Move' : 'Attach'} <strong>{recording.name}</strong> to <strong>{selected.name}</strong>
                </p>
                <p style={{margin: 0, fontSize: 13, color: '#5c7080'}}>
                    This will assign <strong>{recording.name}</strong> ({recording.id}) to device <strong>{selected.name}</strong>.
                    {currentDeviceName && <> It will be removed from <strong>{currentDeviceName}</strong>.</>}
                </p>
                <Button
                    fill
                    intent="primary"
                    icon={currentDeviceName ? 'arrow-right' : 'add'}
                    text={loading ? (currentDeviceName ? 'Moving...' : 'Attaching...') : `${currentDeviceName ? 'Move' : 'Attach'} to ${selected.name}`}
                    disabled={loading}
                    onClick={handleMove}
                />
            </div>
        );
    }

    if (subStep === MOVE_DONE) {
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75em', textAlign: 'center'}}>
                <Icon icon="tick-circle" size={32} intent="success" />
                <p style={{margin: 0}}>
                    <strong>{recording.name}</strong> has been {currentDeviceName ? 'moved' : 'attached'} to <strong>{selected.name}</strong>.
                </p>
                <Button fill text="Done" onClick={() => window.location.reload()} />
            </div>
        );
    }

    return null;
};

const DETACH_CONFIRM = 'confirm';
const DETACH_DONE = 'done';

const DetachStep = ({recording}) => {
    const [subStep, setSubStep] = useState(DETACH_CONFIRM);
    const [loading, setLoading] = useState(false);

    const currentDeviceName = recording.cameraServer?.name;

    const handleDetach = useCallback(() => {
        setLoading(true);
        recordings.update(recording.id, {cameraServer: null})
            .then(() => {
                successToast({message: `Detached ${recording.name} from ${currentDeviceName}`});
                // Fire-and-forget: notify device to update its config
                if (currentDeviceName) {
                    device.command('UpdateHandledRecids', currentDeviceName, null, 'update').catch(() => {});
                    successToast({message: 'Sent device update commands'});
                }
                setSubStep(DETACH_DONE);
            })
            .catch(err => {
                errorToast({message: err?.message ?? 'Failed to detach recording'});
            })
            .finally(() => setLoading(false));
    }, [recording, currentDeviceName]);

    if (subStep === DETACH_CONFIRM) {
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75em'}}>
                <p style={{margin: 0}}>
                    <strong>Step 1:</strong> Detach <strong>{recording.name}</strong> from <strong>{currentDeviceName}</strong>
                </p>
                <p style={{margin: 0, fontSize: 13, color: '#5c7080'}}>
                    This recording ({recording.id}) will be set to have no device. You can reassign it later.
                </p>
                <Button
                    fill
                    intent="warning"
                    icon="remove"
                    text={loading ? 'Detaching...' : `Detach from ${currentDeviceName}`}
                    disabled={loading}
                    onClick={handleDetach}
                />
            </div>
        );
    }

    if (subStep === DETACH_DONE) {
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75em', textAlign: 'center'}}>
                <Icon icon="tick-circle" size={32} intent="success" />
                <p style={{margin: 0}}>
                    <strong>{recording.name}</strong> has been detached from <strong>{currentDeviceName}</strong>.
                </p>
                <Button fill text="Done" onClick={() => window.location.reload()} />
            </div>
        );
    }

    return null;
};

const MoveRecordingDialog = ({recording}) => {
    const [isOpen, setIsOpen] = useState(false);
    const hasDevice = !!recording.cameraServer;
    const [step, setStep] = useState(hasDevice ? STEP_CHOICE : STEP_MOVE);
    const hasOrgAndLocation = !!recording.organisation?._id && !!recording.location?._id;

    const handleOpen = useCallback(() => {
        setStep(hasDevice ? STEP_CHOICE : STEP_MOVE);
        setIsOpen(true);
    }, [hasDevice]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    const title = !hasDevice
        ? 'Attach to device'
        : step === STEP_CHOICE
            ? 'Move or detach recording'
            : step === STEP_MOVE
                ? 'Move recording'
                : 'Detach recording';

    const disabledReason = !hasOrgAndLocation
        ? `This recording must have an organisation and location before it can be ${hasDevice ? 'moved' : 'attached to a device'}.`
        : null;

    const button = (
        <Button
            fill
            text={hasDevice ? `Move or detach from ${recording.cameraServer.name}` : 'Attach to a device'}
            intent="danger"
            icon={hasDevice ? 'remove' : 'add'}
            onClick={handleOpen}
            disabled={!!disabledReason}
        />
    );

    return (
        <>
            {disabledReason ? (
                <Tooltip fill content={disabledReason} position="top">
                    {button}
                </Tooltip>
            ) : button}
            <Dialog
                isOpen={isOpen}
                onClose={handleClose}
                title={title}
                icon={!hasDevice ? 'add' : step === STEP_MOVE ? 'arrow-right' : 'remove'}
            >
                <DialogBody>
                    {step === STEP_CHOICE && (
                        <div style={{display: 'flex', flexDirection: 'column', gap: '0.75em'}}>
                            <p style={{margin: 0}}>
                                Move this recording to another device, or detach from <strong>{recording.cameraServer.name}</strong>?
                            </p>
                            <div style={{display: 'flex', gap: '0.75em'}}>
                                <Button
                                    fill
                                    icon="arrow-right"
                                    text="Move"
                                    intent="primary"
                                    onClick={() => setStep(STEP_MOVE)}
                                />
                                <Button
                                    fill
                                    icon="remove"
                                    text="Detach"
                                    intent="danger"
                                    onClick={() => setStep(STEP_DETACH)}
                                />
                            </div>
                        </div>
                    )}
                    {step === STEP_MOVE && (
                        <MoveStep
                            recording={recording}
                            onClose={handleClose}
                        />
                    )}
                    {step === STEP_DETACH && (
                        <DetachStep
                            recording={recording}
                        />
                    )}
                </DialogBody>
            </Dialog>
        </>
    );
};

export {MoveRecordingDialog};
